import { store } from "@store";
import { openDialog } from "@store/dialogs";
import { CreateStoryDialog } from "@/dialogs/create-story-dialog";
import { DEFAULT_FAVICON, DEFAULT_STORY } from "@/shared/constants";

interface IProps {
  json: string;
  folderId?: string;
}

interface IBuildTemplateStoryFromSlidesProps {
  slides: unknown[];
  title?: string;
  folderId?: string | null;
}

/**
 * Build a story object suitable for CreateStoryDialog from a slides array
 * produced by the presentation-generation backend. Reuses DEFAULT_STORY's
 * settings (designSystemV2, og, engagement bar, etc.) so generated decks land
 * in the editor with sensible defaults the user can change later.
 */
export const buildTemplateStoryFromSlides = ({
  slides,
  title,
  folderId,
}: IBuildTemplateStoryFromSlidesProps) => {
  const cloned = structuredClone(DEFAULT_STORY) as typeof DEFAULT_STORY & {
    folderId?: string | null;
    _id?: string;
  };
  cloned.slides = slides as typeof DEFAULT_STORY.slides;
  if (title) cloned.settings.title = title;
  if (folderId !== undefined) cloned.folderId = folderId;
  cloned._id = "";
  return cloned;
};

export const importStoryFromJson = ({ json, folderId }: IProps) => {
  try {
    const parsedStory = JSON.parse(json);
    if (!parsedStory) return alert("Not a valid json");

    const templateStory = makeNewStoryObj({
      folderId: folderId,
      slides: parsedStory.slides,
      ogSiteName: parsedStory.settings.og.siteName,
      ogDescription: parsedStory.settings.og.description,
      ogLocale: parsedStory.settings.og.locale,
      title: parsedStory.settings.title,
      designSystemV2: parsedStory.settings.designSystemV2,
    });

    store.dispatch(
      openDialog({
        id: CreateStoryDialog.dialogId,
        content: CreateStoryDialog,
        props: {
          templateStory,
          withFolder: true,
          forceUseTemplateSlides: true,
        },
      }),
    );
  } catch (e) {
    console.error(e);
    alert("Error parsing json");
  }
};

interface IObjProps {
  slides: any[];
  designSystemV2: Record<string, unknown>;
  ogSiteName: string;
  ogLocale: string;
  ogDescription: string;
  title: string;
  folderId?: string | null;
  layoutVersion?: string;
}

export const makeNewStoryObj = ({
  folderId,
  title,
  ogSiteName,
  ogDescription,
  ogLocale,
  slides,
  designSystemV2,
  layoutVersion,
}: IObjProps) => {
  return {
    folderId,
    settings: {
      title,
      canonical: "c45c485-f80-8b28-7008-68a3e875234",
      favicon: DEFAULT_FAVICON,
      engagementBarEnabled: true,
      jwtTokenIat: null,
      og: {
        siteName: ogSiteName,
        locale: ogLocale,
        description: ogDescription,
        image:
          "https://www.storydoc.com/assets/images/branding/og-storydoc.gif",
      },
      fs: {
        "fs-vars": "[]",
      },
      engagementBar: {
        presetURL:
          "https://assets.storydoc.com/8011fa118d5708e6/engagement-bar/64a6c20987c0e7000bc4ec7a.json",
        presetId: "64a6c20987c0e7000bc4ec7a",
        active: true,
      },
      gatedContent: null,
      contentPointer: null,
      publicContentPointer: null,
      designSystemV2,
    },
    layoutVersion: layoutVersion || "5.0.0",
    status: "Draft",
    _id: "",
    slides,
  };
};
